import { useState } from 'react';
import { Transition } from '@headlessui/react';
import { useMediaQuery } from 'react-responsive';

import Image from '../../Atoms/Image';
import Title from '../../Atoms/Title';
import DoubleLineText from '../../Atoms/DoubleLineText';
import Button from '../../Atoms/Button';
import svg from '../../../constants/svg';

import introBg from '../../../assets/img/work-at-intro-bg.webp';
import introModel from '../../../assets/img/work-at-header-model.webp';
import contentImg01 from '../../../assets/img/work-at-synsel-content-img01.webp';
import contentImg02 from '../../../assets/img/work-at-synsel-content-img02.webp';
import contentImg03 from '../../../assets/img/work-at-synsel-content-img03.webp';
import contentImg04 from '../../../assets/img/work-at-synsel-content-img04.webp';
import contentImg05 from '../../../assets/img/work-at-synsel-content-img05.webp';

import meetBg from '../../../assets/img/work-at-meet-bg.webp';

import DownPointer from '../../Atoms/DownPointer';
import Text from '../../Atoms/Text';
import PhotoCollage from '../../Molecules/PhotoCollage';

import collageImg01 from '../../../assets/img/work-collage01.webp';
import collageImg02 from '../../../assets/img/work-collage02.webp';
import collageImg03 from '../../../assets/img/work-collage03.webp';
import collageImg04 from '../../../assets/img/work-collage04.webp';
import collageImg05 from '../../../assets/img/work-collage05.webp';
import collageImg06 from '../../../assets/img/work-collage06.webp';
import collageImg07 from '../../../assets/img/work-collage07.webp';
import collageImg08 from '../../../assets/img/work-collage08.webp';
import collageImg09 from '../../../assets/img/work-collage09.webp';
import collageImg10 from '../../../assets/img/work-collage10.webp';
import collageImg11 from '../../../assets/img/work-collage11.webp';
import collageImg12 from '../../../assets/img/work-collage12.webp';
import VideoBox from '../../Molecules/VideoBox';
import CardVacancyExt from '../../Molecules/CardVacancyExt';
import Pagination from '../../Molecules/Pagination';
import FormFile from '../../Molecules/FormFile';
import Checkbox from '../../Atoms/Checkbox';
import GridBenefits from '../../Molecules/GridBenefits';
import CardContact from '../../Molecules/CardContact';
import MarqueeTeam from '../../Organisms/MarqueeTeam';
import imgPerson from '../../../assets/img/card-person01.jpg';
import imgPerson2 from '../../../assets/img/card-person02.jpg';
import imgPerson3 from '../../../assets/img/card-person03.jpg';

function WorkAt() {
  const [isFilterOpen, setIsFilterOpen] =
    useState<boolean>(false);

  const isMobile = useMediaQuery({
    query: `(max-width: 1023px)`
  });
  return (
    <div>
      <section className="relative">
        <Image src={introBg} cover className="z-[1]" />
        <div className="relative z-[5] bg-sl-blue bg-opacity-80">
          <div className="relative z-10 mx-auto flex max-w-7xl flex-col justify-center px-7 py-28 md:min-h-screen md:py-32 lg:py-44">
            <div className="absolute top-20 right-4 z-[1] w-28 lg:w-64">
              {svg.workHeaderDecor1}
            </div>
            <div className="absolute bottom-6 -left-14 z-[1] w-48 xsm:-left-4 lg:bottom-auto lg:-left-28 lg:top-56 lg:w-auto lg:max-w-lg">
              {svg.workHeaderDecor2}
            </div>
            <div className="absolute inset-0 z-[2] flex flex-col items-end justify-end overflow-hidden xsm:pr-[5%] md:items-center md:pr-0 md:pl-[20%]">
              <Image
                src={introModel}
                className="relative max-h-[75%] md:max-h-[90%]"
              />
            </div>
            <div className="relative z-10">
              <Title
                as="h1"
                color="white"
                weight={900}
                className="mb-10"
              >
                <DoubleLineText
                  className="text-5xl xsm:text-6xl md:text-[80px] lg:text-[120px] lg:leading-none"
                  lines={[
                    <span key="1" className="flex flex-col">
                      <span>Level up</span>
                      <span>your job</span>
                    </span>,
                    <mark key="2">Join Synsel</mark>
                  ]}
                />
              </Title>
              <Button
                iconName="arrow-down"
                shadow
                color="accent"
                text="Bekijk alle vacatures"
              />
            </div>
            <DownPointer className="absolute left-1/2 bottom-5 z-10 -translate-x-1/2 md:bottom-10" />
          </div>
        </div>
      </section>
      <section className="relative z-[5] bg-sl-blue py-20 text-white">
        <div className="relative mx-auto max-w-7xl px-7">
          <div className="relative z-10">
            <div className="pointer-events-none absolute -right-6 -top-28 z-[1] w-24 md:-right-20 md:-top-44 md:w-44 xl:w-64">
              {svg.workContentDecor1}
            </div>

            <div className="absolute -left-2 -bottom-20 -z-[1] hidden w-48 -rotate-90 lg:block">
              {svg.workHeaderDecor1}
            </div>

            <Title
              as="h2"
              weight={900}
              size={1}
              color="inherit"
              className="mb-14 text-center md:mb-24"
            >
              Werken bij Synsel
            </Title>

            <div className="relative mb-14 flex flex-col last:mb-0 md:flex-row-reverse md:items-center">
              <div className="-mr-7 ml-7 mb-8 basis-3/5 self-end overflow-hidden rounded-tl-xl rounded-bl-xl md:-mr-0 md:ml-0 md:mb-0 md:self-auto md:rounded-xl">
                <Image
                  src={contentImg01}
                  className="w-full"
                />
              </div>
              <div className="max-w-lg basis-2/5 self-center md:max-w-none md:self-auto md:pr-10">
                <Title
                  color="white"
                  className="mb-2 md:mb-4 lg:mb-7"
                  as="h2"
                  weight={900}
                >
                  Business first
                </Title>
                <Text
                  font="circular"
                  color="white"
                  size="sm"
                >
                  Wij zijn zo succesvol omdat we bovenop de
                  handel zitten. We reageren direct; als er
                  geld valt te verdienen, dan zitten we er
                  bovenop. Zij betalen uiteindelijk ons
                  salaris dus we zijn scherp, alert,
                  klantvriendelijk en best wel competitief.
                </Text>
              </div>
              <div className="pointer-events-none absolute -left-20 -top-36 z-[1] hidden rotate-[270deg] md:block md:w-44 lg:-top-32 xl:w-64">
                {svg.workContentDecor1}
              </div>
            </div>
            <div className="relative mb-14 flex flex-col md:flex-row md:items-center lg:ml-10">
              <div className="-ml-7 mr-7 mb-8 basis-2/5 self-start overflow-hidden rounded-tr-xl rounded-br-xl md:-ml-0 md:mr-0 md:mb-0 md:self-auto md:rounded-xl">
                <Image
                  src={contentImg02}
                  className="w-full"
                />
              </div>
              <div className=" max-w-lg basis-3/5 self-center md:self-auto md:pl-10 lg:pl-24">
                <Title
                  color="white"
                  as="h2"
                  weight={900}
                  className="mb-2 md:mb-4 lg:mb-7"
                >
                  Continu verbeteren
                </Title>
                <Text
                  font="circular"
                  color="white"
                  size="sm"
                >
                  Onze ambities zijn groot en om deze te
                  bereiken willen we ons werk beter doen,
                  elke dag weer. Het ontwikkelen van jouw
                  talent staat hierin centraal. We schakelen
                  snel met jou over wat er beter kan, wat er
                  goed gaat en waar jij trots op mag zijn
                  zodat je het maximale uit jouw
                  persoonlijke ambities kan halen.
                </Text>
              </div>
              <div className="pointer-events-none absolute right-16 top-2/3 z-[1] mt-48 w-20 md:right-24 md:top-1/2 md:mt-36 md:w-64">
                {svg.workHeaderDecor1}
              </div>
            </div>
            <div className="relative z-10 mb-14 flex flex-col last:mb-0 md:flex-row-reverse md:items-center">
              <div className="-mr-7 ml-7 mb-8 basis-3/5 self-end overflow-hidden rounded-tl-xl rounded-bl-xl md:-mr-0 md:ml-0 md:mb-0 md:self-auto md:rounded-xl lg:basis-2/3">
                <Image
                  src={contentImg03}
                  className="w-full"
                />
              </div>
              <div className="max-w-lg basis-2/5 self-center md:self-auto md:pr-10 lg:basis-1/3 lg:pr-20">
                <Title
                  color="white"
                  as="h2"
                  weight={900}
                  className="mb-2 md:mb-4 lg:mb-7"
                >
                  Serious business
                </Title>
                <Text
                  font="circular"
                  color="white"
                  size="sm"
                >
                  <span>
                    Het is jouw baan om te helpen bij het
                    vinden van die{' '}
                    <a href="#!">beste baan</a> voor onze
                    kandidaten. Je hebt een grote invloed op
                    de baan en het inkomen van onze
                    kandidaten. We zijn in voor een geintje,
                    maar als het over ons werk gaat moet
                    daarom de knop om en is het serious
                    business.
                  </span>
                </Text>
              </div>
            </div>
            <div className="relative mb-14 flex flex-col last:mb-0 md:flex-row md:items-center lg:ml-10">
              <div className="relative z-10 -ml-7 mr-7 mb-8 basis-2/5 self-start overflow-hidden rounded-tr-xl rounded-br-xl md:-ml-0 md:mr-0 md:mb-0 md:self-auto md:rounded-xl">
                <Image
                  src={contentImg04}
                  className="w-full"
                />
              </div>
              <div className="max-w-lg basis-3/5 self-center md:self-auto md:pl-10 lg:pl-24">
                <Title
                  color="white"
                  as="h2"
                  weight={900}
                  className="mb-2 md:mb-4 lg:mb-7"
                >
                  Jij bent verantwoordelijk
                </Title>
                <Text
                  font="circular"
                  color="white"
                  size="sm"
                >
                  We bieden je alles wat je nodig hebt om je
                  werk goed te doen en de ruimte om het
                  beste uit je zelf te halen. Samen met jou
                  lopen we voorop in de recruitment, werken
                  we aan mooie projecten voor de grootste
                  bedrijven van Nederland en schudden we de
                  markt op. Overal liggen kansen die we
                  moeten pakken en deze verantwoordelijkheid
                  grijp je met beide handen aan.
                </Text>
              </div>
              <div className="pointer-events-none absolute top-3/4 -left-10 z-[1] w-48 max-w-none md:-top-52 md:-left-80 md:w-auto md:max-w-lg lg:-left-96">
                {svg.workContentDecor2}
              </div>
            </div>
            <div className="relative mb-14 flex flex-col last:mb-0 md:flex-row-reverse md:items-center">
              <div className="relative z-10 -mr-7 ml-7 mb-8 basis-3/5 self-end overflow-hidden rounded-tl-xl rounded-bl-xl md:-mr-0 md:ml-0 md:mb-0 md:self-auto md:rounded-xl">
                <Image
                  src={contentImg05}
                  className="w-full"
                />
              </div>
              <div className="max-w-lg basis-2/5 self-center md:max-w-none md:self-auto md:pr-10">
                <Title
                  color="white"
                  as="h2"
                  weight={900}
                  className="mb-2 md:mb-4 lg:mb-7"
                >
                  Heb het leuk!
                </Title>
                <Text
                  font="circular"
                  color="white"
                  size="sm"
                >
                  Het belangrijkste van allemaal; heb het
                  leuk! Dit doen we niet alleen met meerdere
                  feestjes, uitjes en een
                  wintersportvakantie. Ook op kantoor is het
                  alsof je met je vrienden aan het werk
                  bent. Deze sfeer is voor ons het
                  belangrijkste wat er is.
                </Text>
              </div>
              <div className="absolute -bottom-24 -right-3 z-[1] w-48 max-w-none rotate-[270deg] lg:-right-12 lg:-bottom-44 lg:w-auto lg:max-w-xs xl:-bottom-72 xl:max-w-lg">
                {svg.workContentDecor2}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="relative z-0">
        <Image src={meetBg} cover className="z-0" />
        <div className="relative z-10 bg-sl-blue bg-opacity-80 pb-64 pt-14 md:pt-16 lg:pt-36 xl:pt-48">
          <div className="mx-auto max-w-7xl px-7 text-center">
            <Title
              as="h2"
              weight={900}
              color="white"
              className="mb-7"
            >
              <span className="text-[48px] !leading-none xsm:text-[60px] md:text-[80px] lg:text-[100px] xl:text-[140px]">
                Meet the <mark>team</mark>
              </span>
            </Title>
            <Text
              weight={500}
              color="white"
              font="circular"
              size="lg"
              className="mx-auto max-w-2xl"
            >
              Met een team van bijna 50 collega&lsquo;s die
              voelen als vrienden wordt er hard gewerkt aan
              mooie projecten voor de grootste bedrijven van
              Nederland.
            </Text>
          </div>
        </div>
      </section>
      <section className="bg-sl-blue pt-0 pb-16 md:pb-28 lg:pb-36">
        <MarqueeTeam
          className="-mt-36"
          slides={[
            {
              img: { src: imgPerson },
              title: 'Anne Pelgrum',
              subtitle: 'Team Lead'
            },
            {
              img: { src: imgPerson2 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson3 },
              title: 'Anne Pelgrum',
              subtitle:
                'Manager Candidate Relations and Reactivation'
            },
            {
              img: { src: imgPerson },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson3 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson2 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson2 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson3 },
              title: 'Anne Pelgrum',
              subtitle:
                'Manager Candidate Relations and Reactivation'
            },
            {
              img: { src: imgPerson },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson3 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson2 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson3 },
              title: 'Anne Pelgrum',
              subtitle:
                'Manager Candidate Relations and Reactivation'
            },
            {
              img: { src: imgPerson2 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson2 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson3 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson },
              title: 'Anne Pelgrum',
              subtitle:
                'Manager Candidate Relations and Reactivation'
            },
            {
              img: { src: imgPerson2 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson3 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson2 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson3 },
              title: 'Anne Pelgrum',
              subtitle:
                'Manager Candidate Relations and Reactivation'
            },
            {
              img: { src: imgPerson },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson3 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            },
            {
              img: { src: imgPerson2 },
              title: 'Anne Pelgrum',
              subtitle: 'Consultant'
            }
          ]}
        />
        <div className="px-7 pb-24 text-center">
          <Button
            as="a"
            href="#!"
            color="white-outline"
            text="Ontmoet alle collega's"
          />
        </div>
        <div className="mx-auto max-w-7xl px-7">
          <Title
            as="h2"
            size={1}
            color="white"
            className="relative z-10 mx-auto mb-8 max-w-3xl xl:mb-12 xl:text-center"
          >
            Level up your job!
          </Title>
          <div className="relative z-0 mx-auto flex max-w-3xl flex-col text-left xl:max-w-none xl:flex-row xl:items-center">
            <div className="absolute -bottom-14 -right-8 w-40 sm:-bottom-24 lg:-bottom-48 lg:-right-48 lg:w-[600px]">
              {svg.workContentDecor3}
            </div>
            <div className="relative z-10 pb-10 xl:w-5/12 xl:pr-10 xl:pb-0">
              <div className="absolute bottom-full -left-8 z-[1] mb-14 w-20 -rotate-90 md:w-28 lg:mb-20 xl:-left-16 xl:w-48">
                {svg.workHeaderDecor1}
              </div>
              <Text
                color="white"
                size="base"
                font="circular"
                weight={500}
              >
                Wij werken hard en gaan altijd voor het
                beste resultaat. Wij kennen de markt goed,
                zijn ambitieus en leveren kwaliteit. Onze
                aanpak is no-nonsense en geven elkaar
                doorlopend feedback, met maar een doel;
                continu verbeteren. Wat we zeggen, doen we
                ook echt. Intern, richting collega’s, maar
                ook extern, richting onze kandidaten en
                opdrachtgevers. Je krijgt veel vrijheid en
                daarmee ook eigen verantwoordelijkheid.
                Samenwerken in een prettige sfeer vinden wij
                belangrijk. Samen zijn wij verantwoordelijk
                voor plezier in het werk!
              </Text>
            </div>
            <div className="w-full xl:w-7/12">
              <VideoBox url="https://www.youtube.com/watch?v=QWl-6WXVvrs" />
            </div>
          </div>
        </div>
      </section>
      <section className="bg-white pt-14 pb-14 xsm:pb-[18vw] lg:pt-20">
        <div className="mx-auto max-w-7xl px-7">
          <GridBenefits />
        </div>
      </section>
      <section className="relative bg-sl-blue pt-14 pb-14 xsm:pt-[32vw] md:pt-[18vw]">
        <div className="absolute left-0 top-0 right-0 -mt-[15vw] hidden xsm:block">
          <PhotoCollage
            images={[
              { src: collageImg01 },
              { src: collageImg02 },
              { src: collageImg03 },
              { src: collageImg04 },
              { src: collageImg05 },
              { src: collageImg06 },
              { src: collageImg07 },
              { src: collageImg08 },
              { src: collageImg09 },
              { src: collageImg10 },
              { src: collageImg11 },
              { src: collageImg12 }
            ]}
          />
        </div>
        <div className="mx-auto max-w-7xl px-7">
          <div className="lg:flex">
            <span className="hidden w-4/12 lg:block" />
            <Title
              as="h2"
              size={1}
              weight={900}
              color="white"
              className="mb-10 text-center lg:text-left"
            >
              Join Synsel
            </Title>
          </div>
          <div className="lg:flex">
            <aside className=" mb-10 lg:mb-0 lg:w-4/12 lg:pr-12">
              <div className="mb-7 flex flex-wrap items-center justify-between gap-5">
                <Title as="h3" weight={900} color="white">
                  <span className="text-2xl xss:text-3xl xl:text-4xl">
                    12 <mark>vacatures</mark>
                  </span>
                </Title>
                <Button
                  text="Filter"
                  className="inline-flex lg:hidden"
                  iconName="sliders-up"
                  onClick={() =>
                    setIsFilterOpen(!isFilterOpen)
                  }
                />
              </div>
              <Transition
                show={!isMobile || isFilterOpen}
                enter="transition-opacity duration-75"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-30"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="lg:max-w-[280px]">
                  <div className="mb-6 hidden lg:block">
                    <FormFile theme="light" />
                  </div>
                  <div>
                    <div className="mb-3 last:mb-0">
                      <Checkbox
                        defaultChecked
                        fluid
                        reverse
                        theme="light"
                      >
                        Sales
                      </Checkbox>
                    </div>
                    <div className="mb-3 last:mb-0">
                      <Checkbox fluid reverse theme="light">
                        Marketing
                      </Checkbox>
                    </div>
                    <div className="mb-3 last:mb-0">
                      <Checkbox fluid reverse theme="light">
                        Customer Success
                      </Checkbox>
                    </div>
                    <div className="mb-3 last:mb-0">
                      <Checkbox fluid reverse theme="light">
                        Data
                      </Checkbox>
                    </div>
                    <div className="mb-3 last:mb-0">
                      <Checkbox fluid reverse theme="light">
                        Stage
                      </Checkbox>
                    </div>
                  </div>
                </div>
              </Transition>
            </aside>
            <div className="lg:w-8/12">
              <div className=" mb-9 flex flex-col gap-y-8">
                <CardVacancyExt
                  title="Junior Recruiting Consultant"
                  description="Wil jij je eigen salaris bepalen, werken in een high-class sales omgeving met de leukste collega’s en deals sluiten met de groots Wil jij je eigen salaris bepalen, werken in een high-class sales omgeving met de leukste collega’s en deals sluiten met de groots"
                  link="#!"
                  linkDirectApply="#!!"
                  badges={[
                    {
                      text: 'Net binnen!',
                      size: 'sm',
                      color: 'red'
                    }
                  ]}
                  tags={[
                    {
                      icon: 'award'
                    },
                    {
                      icon: 'location',
                      text: 'Apeldoorn'
                    },
                    {
                      icon: 'euro',
                      text: '2.300 - 4.822'
                    }
                  ]}
                />
                <CardVacancyExt
                  title="Junior Recruiting Consultant"
                  description="Wil jij je eigen salaris bepalen, werken in een high-class sales omgeving met de leukste collega’s en deals sluiten met de groots Wil jij je eigen salaris bepalen, werken in een high-class sales omgeving met de leukste collega’s en deals sluiten met de groots"
                  link="#!"
                  linkDirectApply="#!!"
                  tags={[
                    {
                      icon: 'award'
                    },
                    {
                      icon: 'location',
                      text: 'Apeldoorn'
                    },
                    {
                      icon: 'euro',
                      text: '2.300 - 4.822'
                    }
                  ]}
                />
                <CardVacancyExt
                  title="Afstudeerstage Marktonderzoek HBO"
                  link="#!"
                  linkDirectApply="#!!"
                  tags={[
                    {
                      icon: 'award'
                    },
                    {
                      icon: 'location',
                      text: 'Apeldoorn'
                    },
                    {
                      icon: 'euro',
                      text: '500'
                    }
                  ]}
                />
              </div>
              <Pagination simple />
            </div>
          </div>
          <div className="mx-auto max-w-sm pt-10 lg:hidden">
            <Title as="h3" color="white" className="mb-3">
              Level up your job
            </Title>
            <Text
              color="white"
              size="lg"
              font="circular"
              className="mb-8"
            >
              Upload je CV en wij vinden de beste baan voor
              jou.
            </Text>
            <FormFile />
          </div>
        </div>
      </section>
      <section className="relative bg-white py-10 lg:py-10">
        <div className="mx-auto w-full max-w-7xl px-7">
          <Title as="h2" className="text-center">
            Wij staan voor je klaar
          </Title>

          <div className="grid gap-3 py-5 sm:grid-cols-2 sm:gap-5 md:grid-cols-4 lg:gap-7">
            <CardContact
              title="Bel"
              description="Direct contact"
              text="088 - 3231300"
              icon="phone"
              urlType="tel"
              url="#!"
            />
            <CardContact
              title="Chat"
              description="Direct antwoord"
              text="Chat met ons"
              icon="comments"
              url="#!"
            />
            <CardContact
              title="App"
              description="Binnen 2 uur reactie"
              text="0619734306"
              icon="whatsapp"
              url="#!"
            />
            <CardContact
              title="Mail"
              description="Binnen 1 dag reactie"
              text="talent@synsel.nl"
              icon="mail"
              url="#!"
              urlType="mail"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

export default WorkAt;
